import React, { useEffect, useMemo, useState } from "react";
import { Avatar, Drawer, Icon, Input, List, Tag, message, notification } from "antd";
import axios from "axios";

import "./DrawerPesquisarAluno.scss";

const openNotificationWithIcon = (type, message, description) => {
    notification[type]({
        message: message,
        description: description
    });
};

const normalize = value =>
    String(value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();

const initials = name => {
    const parts = name.trim().split(/\s+/);
    const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
    return (parts[0][0] + last).toUpperCase();
};

const StudentPickerDrawer = ({ visible, onClose, onConfirm }) => {
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [selectedId, setSelectedId] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const loadStudents = () => {
        axios({
            method: "get",
            url: "/api/turma/carregar-lista-alunos",
            params: {
                turmaId: localStorage.getItem("codigo_curso")
            }
        })
            .then(response => {
                setStudents(response.data);
            })
            .catch(error => {
                openNotificationWithIcon("error", "Erro", error.response?.data ?? "Ocorreu um erro ao carregar os alunos.");
            })
            .finally(() => {
                setLoading(false);
            });
    };

    useEffect(() => {
        if (!visible) return undefined;
        let cancelled = false;

        setSearch("");
        setSelectedId(null);
        setLoading(true);

        loadStudents();
    }, [visible]);

    const filtered = useMemo(() => {
        const query = normalize(search);
        if (!query) return students;
        return students.filter(s => normalize(s.nomeCompleto).includes(query) || normalize(s.numero).includes(query));
    }, [students, search]);

    const selected = students.find(s => s.id === selectedId);

    const select = student => {
        if (!student.hasFolder) setSelectedId(student.id);
    };

    const handleConfirm = async () => {
        setSubmitting(true);

        axios({
            method: "post",
            url: "/api/ensino-especial/adicionar-pasta",
            data: {
                turmaId: localStorage.getItem("codigo_curso"),
                alunoId: selectedId
            }
        })
            .then(() => {
                openNotificationWithIcon("success", "Sucesso", "Pasta criada com sucesso.");
                onConfirm();
                onClose();
            })
            .catch(error => {
                openNotificationWithIcon("error", "Erro", error.response?.data ?? "Não foi possível criar a pasta. Tenta novamente.");
            })
            .finally(() => {
                setSubmitting(false);
            });
    };

    const renderItem = student => {
        const isSelected = student.id === selectedId;
        const className = [
            "student-picker__item",
            isSelected && "student-picker__item--selected",
            student.hasFolder && "student-picker__item--disabled"
        ]
            .filter(Boolean)
            .join(" ");

        return (
            <List.Item
                className={className}
                role="button"
                aria-pressed={isSelected}
                aria-disabled={!!student.hasFolder}
                tabIndex={student.hasFolder ? -1 : 0}
                onClick={() => select(student)}
                onKeyDown={e => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        select(student);
                    }
                }}
            >
                <List.Item.Meta
                    avatar={
                        <Avatar size="large" src={student.foto}>
                            {initials(student.nomeCompleto)}
                        </Avatar>
                    }
                    title={student.nomeCompleto}
                    description={`N.º ${student.numero}`}
                />
                {student.hasFolder && <Tag>Já tem pasta</Tag>}
                {isSelected && <Icon type="check-circle" theme="filled" className="student-picker__check" />}
            </List.Item>
        );
    };

    return (
        <Drawer
            title="Selecionar aluno"
            width={420}
            visible={visible}
            onClose={onClose}
            bodyStyle={{ paddingBottom: 80 }}
            maskClosable={false}
        >
            <div className="bloco-info">
                <div className="student-picker__search">
                    <Input
                        placeholder="Pesquisar por nome ou número"
                        prefix={<Icon type="search" />}
                        allowClear
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                </div>

                <div className="student-picker__list">
                    <List
                        split={false}
                        loading={loading}
                        dataSource={filtered}
                        rowKey="id"
                        renderItem={renderItem}
                        locale={{ emptyText: search ? "Nenhum aluno corresponde à pesquisa" : "Esta turma não tem alunos" }}
                    />
                </div>
            </div>
            <div className="ant-drawer-footer">
                <button className="botao-secundario" onClick={onClose} style={{ marginRight: 20, display: "inline-block" }}>
                    Voltar
                </button>
                <button className="botao-principal" disabled={!selected} loading={submitting} onClick={handleConfirm}>
                    {submitting ? <Icon type="loading" /> : null}
                    Salvar
                </button>
            </div>
        </Drawer>
    );
};

export default StudentPickerDrawer;
